
import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { Card, CardContent } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Building2, 
  Plus, 
  Search, 
  Loader2, 
  MoreHorizontal, 
  Pencil, 
  Copy, 
  Trash,
  X,
  FileSpreadsheet
} from "lucide-react";
import { propertyApi } from "@/services/api";
import { useToast } from "@/hooks/use-toast";
import { Property } from "@/models";
import PropertyFormContent from "@/components/properties/PropertyFormContent";
import { DeletePropertyDialog } from "@/components/properties/DeletePropertyDialog";
import { PropertyBulkUploadModal } from "@/components/properties/PropertyBulkUploadModal";
import { PaginationControls } from "@/components/shared/PaginationControls";

export const AssociationPropertiesTab = ({ association }) => {
  const { id: associationId } = useParams<{ id: string }>();
  const [searchTerm, setSearchTerm] = useState("");
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [propertyToDelete, setPropertyToDelete] = useState<Property | null>(null);
  const [showAddPropertyForm, setShowAddPropertyForm] = useState(false);
  const [isBulkUploadModalOpen, setIsBulkUploadModalOpen] = useState(false);
  const [property, setProperty] = useState<Partial<Property>>({
    address: "",
    association_id: parseInt(associationId || "0"),
    city: association?.default_property_city || "",
    state: association?.default_property_state || "",
    zip_code: association?.default_property_zip || "",
    latitude: association?.default_property_latitude || undefined,
    longitude: association?.default_property_longitude || undefined,
  });
  // Pagination state
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [limit] = useState<number>(10);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Calculate offset based on current page
  const offset = (currentPage - 1) * limit;

  const searchParams = {
    search: searchTerm,
    association_id: parseInt(associationId || "0"),
    limit,
    offset
  };

  const { data: propertiesResponse = { data: [], metadata: { total_count: 0, limit, offset } }, isLoading: isLoadingProperties } = useQuery({
    queryKey: ['properties', searchParams],
    queryFn: propertyApi.list,
    meta: {
      onError: (error: any) => {
        toast({
          variant: "destructive",
          title: "Error loading properties",
          description: error.message || "Failed to fetch properties",
        });
      }
    }
  });

  // Extract properties from the paginated response
  const properties = propertiesResponse.data || [];
  const totalCount = propertiesResponse.metadata?.total_count || 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / limit));

  const deleteMutation = useMutation({
    mutationFn: (id: number) => propertyApi.delete(id),
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Property deleted successfully",
      });
      
      queryClient.invalidateQueries({ queryKey: ['properties'] });
      
      setIsDeleteDialogOpen(false);
      setPropertyToDelete(null);
    },
    onError: (error: any) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to delete property",
      });
    }
  });

  const createMutation = useMutation({
    mutationFn: (data: Partial<Property>) => propertyApi.create(data as Omit<Property, 'id' | 'created_at' | 'updated_at'>),
    onSuccess: () => {
      toast({
        title: "Property created",
        description: "The property has been created successfully."
      });
      queryClient.invalidateQueries({ queryKey: ['properties', searchParams] });
      setShowAddPropertyForm(false);
      setLoading(false);
      resetPropertyState();
    },
    onError: (error: any) => {
      toast({
        title: "Error creating property",
        description: error.message || "Failed to create property",
        variant: "destructive"
      });
      setLoading(false);
    }
  });

  const duplicateMutation = useMutation({
    mutationFn: (propertyToDuplicate: Property) => {
      const newProperty = {
        address: propertyToDuplicate.address,
        association_id: propertyToDuplicate.association_id,
        city: propertyToDuplicate.city,
        state: propertyToDuplicate.state,
        zip_code: propertyToDuplicate.zip_code,
        latitude: propertyToDuplicate.latitude,
        longitude: propertyToDuplicate.longitude,
        suite_unit: propertyToDuplicate.suite_unit,
      };
      
      return propertyApi.create(newProperty as Omit<Property, 'id' | 'created_at' | 'updated_at'>);
    },
    onSuccess: () => {
      toast({
        title: "Property duplicated",
        description: "The property has been duplicated successfully."
      });
      queryClient.invalidateQueries({ queryKey: ['properties', searchParams] });
    },
    onError: (error: any) => {
      toast({
        variant: "destructive",
        title: "Error duplicating property",
        description: error.message || "Failed to duplicate property",
      });
    }
  });

  const resetPropertyState = () => {
    setProperty({
      address: "",
      suite_unit: "",
      association_id: parseInt(associationId || "0"),
      city: association?.default_property_city || "",
      state: association?.default_property_state || "",
      zip_code: association?.default_property_zip || "",
      latitude: association?.default_property_latitude || undefined,
      longitude: association?.default_property_longitude || undefined,
    });
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleDelete = () => {
    if (!propertyToDelete) return;
    deleteMutation.mutate(propertyToDelete.id);
  };

  const handleDuplicate = (property: Property) => {
    duplicateMutation.mutate(property);
  };

  const handleAddProperty = () => {
    setShowAddPropertyForm(true);
    resetPropertyState();
  };

  const handleCancelAddProperty = () => {
    setShowAddPropertyForm(false);
    resetPropertyState();
  };

  const handleSaveProperty = async () => {
    if (!property.address || !property.association_id) {
      toast({
        title: "Validation Error",
        description: "Please fill in all required fields.",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    createMutation.mutate({
      address: property.address!,
      association_id: property.association_id!,
      city: property.city,
      state: property.state,
      zip_code: property.zip_code,
      suite_unit: property.suite_unit,
      latitude: property.latitude,
      longitude: property.longitude
    });
  };

  const handleRowClick = (propertyId: number) => {
    navigate(`/backoffice/properties/${propertyId}/edit?association_id=${associationId}&return_to=association`);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="relative w-full max-w-md">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Search properties..."
            className="pl-8"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="flex space-x-2">
          {!showAddPropertyForm && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button>
                  <Plus className="mr-2 h-4 w-4" /> Add Property
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="center">
                <DropdownMenuItem onClick={handleAddProperty}>
                  <Plus className="mr-2 h-4 w-4" /> Add Single Property
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setIsBulkUploadModalOpen(true)}>
                  <FileSpreadsheet className="mr-2 h-4 w-4" /> Add Multiple Properties
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      {showAddPropertyForm && (
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Add New Property</h3>
            <Button variant="ghost" size="sm" onClick={handleCancelAddProperty}>
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </Button>
          </div>
          <PropertyFormContent
            property={property}
            setProperty={setProperty}
            associations={[association]}
            isLoading={loading}
            onSave={handleSaveProperty}
            showAssociationField={false}
            showSuiteUnitFirst={true}
          />
        </div>
      )}

      <Card>
        <CardContent className="p-0">
          {isLoadingProperties ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-brand-blue" />
            </div>
          ) : properties.length > 0 ? (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Suite/Unit</TableHead>
                    <TableHead>Address</TableHead>
                    <TableHead>City</TableHead>
                    <TableHead>State</TableHead>
                    <TableHead>ZIP Code</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {properties.map((property: Property) => (                     
                    <TableRow 
                      key={property.id} 
                      className="cursor-pointer"
                      onClick={() => handleRowClick(property.id)}
                    >
                      <TableCell>{property.suite_unit || "—"}</TableCell>
                      <TableCell>{property.address}</TableCell>
                      <TableCell>{property.city || "—"}</TableCell>
                      <TableCell>{property.state || "—"}</TableCell>
                      <TableCell>{property.zip_code || "—"}</TableCell>
                      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <span className="sr-only">Open menu</span>
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuLabel>Actions</DropdownMenuLabel>
                            <DropdownMenuItem onClick={() => navigate(`/backoffice/properties/${property.id}/edit?association_id=${associationId}&return_to=association`)}>
                              <Pencil className="mr-2 h-4 w-4" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDuplicate(property)}>
                              <Copy className="mr-2 h-4 w-4" />
                              Duplicate
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              className="text-red-600"
                              onClick={() => {
                                setPropertyToDelete(property);
                                setIsDeleteDialogOpen(true);
                              }}
                            >
                              <Trash className="mr-2 h-4 w-4" />
                              Remove
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Add pagination controls and display */}
              <div className="flex justify-between items-center px-4 py-2 border-t">
                <div className="text-sm text-muted-foreground">
                  {totalCount > 0 ? (
                    `Showing ${offset + 1}-${Math.min(offset + properties.length, totalCount)} of ${totalCount} properties`
                  ) : (
                    "No properties found"
                  )}
                </div>
                
                {totalPages > 1 && (
                  <PaginationControls 
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                  />
                )}
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <Building2 className="mx-auto h-12 w-12 text-muted-foreground/50 mb-4" />
              <h3 className="text-lg font-medium">No properties found</h3>
              <p className="text-sm text-muted-foreground mb-4">
                {searchTerm
                  ? "Try a different search term"
                  : "No properties have been added to this association yet"}
              </p>
              {!showAddPropertyForm && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button>
                      <Plus className="mr-2 h-4 w-4" /> Add Property
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="center">
                    <DropdownMenuItem onClick={handleAddProperty}>
                      <Plus className="mr-2 h-4 w-4" /> Add Single Property
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setIsBulkUploadModalOpen(true)}>
                      <FileSpreadsheet className="mr-2 h-4 w-4" /> Add Multiple Properties
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <DeletePropertyDialog 
        open={isDeleteDialogOpen}
        onOpenChange={(open) => {
          setIsDeleteDialogOpen(open);
          if (!open) {
            setPropertyToDelete(null);
          }
        }}
        onDelete={handleDelete}
        isPending={deleteMutation.isPending}
      />
      
      <PropertyBulkUploadModal
        open={isBulkUploadModalOpen}
        onOpenChange={setIsBulkUploadModalOpen}
        associationId={parseInt(associationId || "0")}
      />
    </div>
  );
};
